import { Injectable } from '@angular/core';
import {
  fetchAuthSession,
  getCurrentUser,
  signIn,
  signInWithRedirect,
  signOut
} from 'aws-amplify/auth';
import { BehaviorSubject } from 'rxjs';

export interface IUser {
  email: string;
  password: string;
  name?: string;
}

@Injectable({
  providedIn: 'root'
})
export class CognitoService {
  private readonly authorizedGroups = ['ADMIN_OPERACIONES', 'ACCESS_DEV', 'EQUIPO_OPERACIONES'];
  private authorizationCache: {
    groups: string[];
    authorized: boolean;
  } | null = null;
  private authenticationSubject: BehaviorSubject<boolean>;

  constructor() {
    this.authenticationSubject = new BehaviorSubject<boolean>(false);
  }

  async signIn(user: IUser): Promise<boolean> {
    try {
      this.clearAuthorizationCache();
      const result = await signIn({
        username: user.email.trim(),
        password: user.password
      });
      const signedIn = result.isSignedIn === true;
      this.authenticationSubject.next(signedIn);
      return signedIn;
    } catch (err) {
      this.authenticationSubject.next(false);
      console.error('Error al iniciar sesión:', err);
      return false;
    }
  }

  async signInWithHostedUi(): Promise<void> {
    this.clearAuthorizationCache();
    await signInWithRedirect();
  }

  async signOut(): Promise<void> {
    if (sessionStorage.getItem('auth_access_denied') !== '1') {
      sessionStorage.setItem('auth_signed_out', '1');
    }
    this.clearStoredAuthContext();
    this.clearAuthorizationCache();
    this.authenticationSubject.next(false);
    await signOut();
  }

  async isAuthenticated(forceRefresh = false): Promise<boolean> {
    try {
      const session = await fetchAuthSession({ forceRefresh });
      const hasValidTokens = !!(
        session.tokens?.accessToken && session.tokens?.idToken
      );

      if (hasValidTokens) {
        this.authenticationSubject.next(true);
        this.persistTokens(session);
        return true;
      } else {
        this.authenticationSubject.next(false);
        return false;
      }
    } catch (error) {
      console.log('No hay sesión Amplify activa');
      this.authenticationSubject.next(false);
      return false;
    }
  }

  public getUser(): Promise<any> {
    return getCurrentUser();
  }

  public getAuthenticatedUser(): Promise<any> {
    return getCurrentUser();
  }

  public async getRol(forceRefresh = false): Promise<string[]> {
    try {
      const session = await fetchAuthSession({ forceRefresh });
      this.persistTokens(session);
      const groups = session.tokens?.idToken?.payload?.['cognito:groups'];
      return Array.isArray(groups) ? groups.map(String) : [];
    } catch (error) {
      console.error('Error al obtener rol:', error);
      return [];
    }
  }

  public async isAuthorized(forceRefresh = false): Promise<boolean> {
    if (!forceRefresh && this.authorizationCache) {
      return this.authorizationCache?.authorized ?? false;
    }

    const groups = await this.getRol(forceRefresh);
    const authorized = groups.some((group) => this.authorizedGroups.includes(group));
    this.authorizationCache = {
      groups,
      authorized
    };
    return authorized;
  }

  public clearAuthorizationCache(): void {
    this.authorizationCache = null;
  }

  public async revalidateAuthorization(): Promise<boolean> {
    this.clearAuthorizationCache();
    return this.isAuthorized(true);
  }

  public async getToken(): Promise<string | null> {
    try {
      const session = await fetchAuthSession();
      this.persistTokens(session);
      return session.tokens?.idToken?.toString() || null;
    } catch (error) {
      console.error('Error al obtener token:', error);
      return null;
    }
  }

  public async getSession(): Promise<any> {
    const session = await fetchAuthSession();
    this.persistTokens(session);
    return session;
  }

  public async hydrateUserContext(): Promise<void> {
    const user = await getCurrentUser();
    const session = await fetchAuthSession();
    this.persistTokens(session);

    const payload = session.tokens?.idToken?.payload || {};
    const middleName = this.asString(payload['middle_name']);
    const familyName = this.asString(payload['family_name']);
    const email =
      this.asString(payload['email']) ||
      user.signInDetails?.loginId ||
      user.username ||
      '';
    const username = user.username || '';

    this.setOrRemoveLocalStorage('nombre', middleName);
    this.setOrRemoveLocalStorage('apellido', familyName);
    this.setOrRemoveLocalStorage('cognito_username', username);
    this.setOrRemoveLocalStorage('email', email);
  }

  // Obtiene el usuario por family_name + middle_name, si uno de los dos no existe, devuelve el correo del usuario como última opción
  async getUsername(): Promise<string> {
    try {
      const authSession = await fetchAuthSession();
      this.persistTokens(authSession);
      const payload = authSession.tokens?.idToken?.payload || ({} as any);
      const { family_name, middle_name, email } = payload;

      if (family_name && middle_name) {
        return `${middle_name} ${family_name}`;
      }

      if (email) {
        return email;
      }

      const { username } =
        authSession.tokens?.accessToken?.payload || ({} as any);
      if (!username) {
        throw new Error('Vuelva a iniciar sesión');
      }

      return username;
    } catch (unknownError) {
      console.error(
        'Error desconocido al obtener el nombre de usuario:',
        unknownError
      );
      throw new Error('Error desconocido al obtener el nombre de usuario');
    }
  }

  private persistTokens(session: any): void {
    const idToken = session.tokens?.idToken?.toString();
    const accessToken = session.tokens?.accessToken?.toString();

    this.setOrRemoveLocalStorage('id_token', idToken);
    this.setOrRemoveLocalStorage('access_token', accessToken);
  }

  public clearStoredAuthContext(): void {
    [
      'id_token',
      'access_token',
      'refresh_token',
      'nombre',
      'apellido',
      'cognito_username',
      'email'
    ].forEach((key) => localStorage.removeItem(key));
  }

  private setOrRemoveLocalStorage(key: string, value: string | null | undefined): void {
    if (value) {
      localStorage.setItem(key, value);
      return;
    }
    localStorage.removeItem(key);
  }

  private asString(value: unknown): string {
    return typeof value === 'string' ? value : '';
  }
}
